import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HelpersService {
  isMobileState: BehaviorSubject<boolean> = new BehaviorSubject(this.isMobile());
  doc;

  //layout variables
  fontbaseMobile = 14;
  fontbaseDesktop = 18;
  maxBreakpoint = 1440;
  containerPaddingMobile = 25;
  containerSizeDesktop = 1270;
  maxContainerDesktop = (this.containerPaddingMobile * 2) + this.containerSizeDesktop;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.doc = document;
  }

  validatePlatform(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  getWindow(): any {
    return (this.validatePlatform()) ? window : undefined;
  }

  get nativeWindow() {
    return this.getWindow();
  }

  isMobile(breakpoint: number = 991): boolean {
    return !(this.nativeWindow && this.nativeWindow.innerWidth >= breakpoint);
  }

  checkWebp(): boolean {
    if (!this.validatePlatform()) return false;

    let elem = this.doc.createElement('canvas');
    if (!!(elem.getContext && elem.getContext('2d'))) {
      // was able or not to get webp representation
      return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
    }

    return false;
  }

  setWebpClass() {
    if (this.checkWebp()) {
      this.doc.documentElement.classList.add('webp');
      this.doc.documentElement.classList.remove('nowebp');
    } else {
      this.doc.documentElement.classList.add('nowebp');
      this.doc.documentElement.classList.remove('webp');
    }
  }

  canUseWebp(): boolean {
    return this.doc.documentElement.classList.contains('webp');
  }

  scrollTo(scrollTop: number) {
    if (!this.validatePlatform()) return;

    this.nativeWindow.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  }

  scrollToItem(selector: string, offset: number = 0) {
    if (!this.validatePlatform()) return;

    const container = (this.doc.querySelector(`#${selector}`) as HTMLElement);

    window.scrollTo({
      behavior: 'smooth',
      top: container.getBoundingClientRect().top + window.scrollY - offset
    })
  }

  setMainPadding() {
    // Set padding to <main> based on header height
    const headerEl = this.doc.querySelector('header') as HTMLElement;
    const headerOffsetH = headerEl?.offsetHeight;
    const mainEl = this.doc.querySelector('#main') as HTMLElement;

    mainEl.style.paddingTop = `${headerOffsetH}px`;
  }

  //Convert measure units functions
  toVW(inPX: number, screen: number = this.maxBreakpoint) {
    let inVW = (inPX / screen) * 100;
    return `${inVW}vw`;
  }

  toVH(inPX: number, screen: number = 1080) {
    let inVH = (inPX / screen) * 100;
    return `${inVH}vh`;
  }

  toREM(inPX: number, fontbase: number = this.fontbaseDesktop) {
    let inREM = inPX / fontbase;
    return `${inREM}rem`;
  }

  toREMMobile(inPX: number, fontbase: number = this.fontbaseMobile) {
    let inREM = inPX / fontbase;
    return `${inREM}rem`;
  }

  setLang(locale: string = 'en-US') {
    this.doc.documentElement.lang = locale;
  }

  addOstroScript(chatID: string) {
    let id= 'rxscript';
    let src = 'https://in.rxengage.app/rxdefine.js/rxdefine.js?id='+chatID;
    let type = 'module';

    //check if ostro is already inserted
    if (this.doc.getElementById(id)) return;

    const script = this.doc.createElement('script');
    script.id = id;
    script.type = type;
    script.src = src;
    script.async = true;

    this.doc.body.appendChild(script);
  }

  setOstroLang(type: string) {
    const enChatID = 'ac844a87-9e6a-450f-a5b5-2089b7e4ad59';
    const esChatID = 'f950fa6d-ad9f-4621-bb7d-993e83adc855';

    this.addOstroScript((type == 'en') ? enChatID : esChatID);

    if (!this.validatePlatform()) return;

    if (window.TAIlor) {
      window.TAIlor.reloadWithId((type == 'en') ? enChatID : esChatID);
    }
  }
}
